<template>
  <TabsRoot
    v-if="displayType === 'standard'"
    v-model="internalState"
    @update:modelValue="$emit('update:modelValue', $event)"
    :orientation="orientation"
    :class="cn(variants({ orientation, variant, fullWidth }), props.class)"
  >
    <div
      v-if="!hideNavigation"
      class="flex items-center justify-between w-full"
    >
      <TabsList
        aria-label="tabs"
        :class="
          cn(
            containerVariants({
              orientation,
              variant,
              tabVariant,
              size,
              fullWidth,
            })
          )
        "
      >
        <slot name="leading" />
        <div
          v-for="(tab, key) of tabLabels"
          :key="key"
          :class="[
            'flex shrink-0',
            {
              'items-center': orientation === 'horizontal',
              'flex-col': orientation === 'vertical',
              grow: fullWidth,
            },
          ]"
        >
          <Tooltip
            side="bottom"
            :title="tab.disabledTooltip"
            :disabled="!tab.disabled"
            disableClosingTrigger
          >
            <TabsTrigger
              :disabled="tab.loading || tab.disabled"
              :value="`${key}`"
              :class="
                cn(
                  tabButtonVariants({
                    orientation,
                    tabVariant,
                    size,
                    fullWidth,
                    active: isActive(key),
                    badge: !!tab.badge,
                    variant,
                    disabled: tab.loading || tab.disabled,
                  })
                )
              "
            >
              <Component
                v-if="tab.is"
                :is="tab.is"
                v-bind="tab.props"
                @click="$emit(tab.event, $event)"
              />
              <template v-else>
                <Icon v-if="tab.icon" :name="tab.icon" size="xl" />
                {{ tab?.label }}
                <Badge
                  v-if="tab.badge"
                  :value="tab.badge"
                  :label="tab.badge"
                  :type="badgeType(key)"
                  color="blue"
                  size="sm"
                />
              </template>
            </TabsTrigger>
          </Tooltip>
          <Divider
            v-if="tab.hasDivider"
            :orientation="
              orientation === 'horizontal' ? 'vertical' : 'horizontal'
            "
            :class="{
              'py-2 ml-1 h-9': orientation === 'horizontal',
              'w-full py-1 h-px': orientation === 'vertical',
            }"
          />
        </div>
      </TabsList>
      <slot name="end" />
    </div>
    <div v-if="!hideContent" :class="contentVariants({ variant })">
      <TabsContent
        v-for="(_, key) of tabLabels"
        :key="key"
        :value="`${key}`"
        asChild
      >
        <Transition name="fade-delayed" appear>
          <Component :is="renderSlotContent(key)" :key="key" />
        </Transition>
      </TabsContent>
    </div>
  </TabsRoot>
  <div v-else-if="displayType === 'menu'">
    <!-- dropdown menu to control tabs v-model="internalValue" -->
    <slot />
  </div>
</template>
<script setup>
/*
    Example Usage:
    <Tabs>
      <Tab label="Tab 1" disabled badge="1">
        <div>Tab 1 content</div>
      </Tab>
    </Tabs>

    Also supports external control via v-model
    <Tabs v-model="activeTab" orientation="vertical">
      <Tab label="Tab 1">
        <div>Tab 1 content</div>
      </Tab>
    </Tabs>

    Or preselected tab with defaultTab prop (number)
 */
import { computed, ref, watch, provide, useSlots, h } from "vue";
import {
  orientationVariants,
  sizes,
  tabVariants,
  variants,
  containerVariants,
  tabButtonVariants,
  contentVariants,
} from ".";
import { cn } from "@/Utils/cn";
import {
  Primitive,
  TabsRoot,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "radix-vue";
import { Badge } from "@/SharedComponents/Badges";
import { Icon } from "@/SharedComponents/Icon";
import { Divider } from "@/SharedComponents/Divider";
import { Tooltip } from "@/SharedComponents/Tooltips";

const slots = useSlots();

defineEmits(["update:modelValue"]);

const props = defineProps({
  modelValue: { type: [String, Number], default: null },
  orientation: {
    type: String,
    validator: (val) => orientationVariants.includes(val),
    default: "horizontal",
  },
  variant: { type: String, required: false },
  tabVariant: {
    type: String,
    validator: (val) => tabVariants.includes(val),
    default: "button-primary",
  },
  size: {
    type: String,
    validator: (val) => sizes.includes(val),
    default: "lg",
  },
  fullWidth: { type: Boolean, default: false },
  defaultTab: { type: [String, Number], default: 0 },
  hideNavigation: { type: Boolean, required: false },
  hideContent: { type: Boolean, required: false },
  class: { type: null, required: false },
});

/*
 * displayType: 'menu' | 'standard'
 * can base it on screen size / device type
 */
const displayType = computed(() => {
  return "standard";
});

const badgeType = (key) => {
  if (
    props.variant === "button-primary" ||
    props.variant === "underline-filled"
  ) {
    return isActive(key) ? "light" : "modern";
  }

  return "modern";
};

/*
   Gather tab information from slots
 */
const tabs = computed(() => {
  return slots && slots.default
    ? slots.default().reduce((tabs, slot) => {
        if (slot.props?.label) {
          tabs.push(slot);
        } else if (Array.isArray(slot.children) && slot.children.length) {
          slot.children.forEach((child) => {
            if (child.props) {
              tabs.push(child);
            }
          });
        }
        return tabs;
      }, [])
    : [];
});

const tabLabels = computed(() => {
  return tabs.value.map((tab) => {
    return {
      ...tab.props,
      identifier: tab.props.identifier || tab.props.label,
    };
  });
});

const internalState = ref(
  props.modelValue !== null
    ? props.modelValue.toString()
    : displayType.value === "menu"
    ? null // not open by default if menu style
    : props.defaultTab.toString() // first tab in classic style
);

const isActive = (tab) => {
  return internalState.value === `${tab}`;
};

const renderSlotContent = (tab) => {
  for (let i = 0; i < tabs.value.length; i++) {
    if (tab === i) {
      return h(Primitive, { asChild: true }, tabs.value[i].children);
    }
  }
};

watch(
  () => props.modelValue,
  (val) => {
    internalState.value = val?.toString();
  }
);

const activeTab = computed(() => {
  if (internalState.value === null) return -1;
  return tabLabels.value[internalState.value];
});

/*
 * Provide active tab to children
 * this is only for displayType: 'menu'
 */
provide("activeTab", activeTab);
</script>
